import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  FormControlLabel,
  Checkbox,
  TextField,
  MenuItem,
  DialogProps,
  InputAdornment,
  CircularProgress,
} from '@mui/material'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCompany } from '@/context/companies'
import {
  BorrowerDataUpdate,
  useCreateBorrower,
  useUpdateBorrower,
} from '@/api/accounting-dealer'
import {
  ReadCompanySerproResult,
  useReadCompanySerpro,
} from '@/api/accounting-dealer/company'
import { readZipcode } from '@/api/accounting-dealer/person'
import { Borrower } from '@/types'
import {
  CNPJ_IMASK_OPTS,
  isValidPhone,
  removeNonNumeric,
  sanitizeCnpj,
  toast,
} from '@/helpers'
import {
  BorrowerRegistrationFormData,
  createBorrowerRegistrationFormSchema,
} from './schema'
import { MaskedInput } from '../../../masked-input'
import { LoadingButton } from '@mui/lab'
import { AddressForm } from '../../../address-form'
import { useEffect, useRef } from 'react'

export function BorrowerRegistrationDialog({
  onSuccess,
  mode = 'create',
  borrower,
  ...props
}: TBorrowerRegistrationDialogProps) {
  const { company } = useCompany()

  const form = useForm<BorrowerRegistrationFormData>({
    resolver: zodResolver(createBorrowerRegistrationFormSchema(mode)),
    defaultValues: DEFAULT_VALUES,
  })

  const isEditMode = mode === 'edit'
  const dialogTitle = isEditMode ? 'Atualizar cliente' : 'Cadastrar cliente'
  const submitButtonText = isEditMode ? 'ATUALIZAR' : 'CADASTRAR'
  const { control, handleSubmit, watch, reset, setValue } = form
  const noAddress = watch('no_address')
  const taxIdType = watch('tax_id_type')
  const cleanTaxId = sanitizeCnpj(watch('tax_id') ?? '')
  const shouldSearchSerpro =
    !isEditMode && taxIdType === 'cnpj' && cleanTaxId.length === 14

  const lastFilledTaxId = useRef('')

  const { data: serproData, isFetching: isLoadingSerpro } =
    useReadCompanySerpro(
      { companyKey: company?.key, taxId: cleanTaxId },
      { enabled: shouldSearchSerpro },
    )

  const fillWithSerproData = (data: ReadCompanySerproResult) => {
    const opts = { shouldValidate: true }
    setValue('name', data.corporate_name, opts)

    const email = data.contacts?.find((c) => c.type === 'email')?.data
    if (email) setValue('email', email, opts)

    const phone = data.contacts?.find(
      (c) => c.type === 'phone' && isValidPhone(c.data),
    )?.data
    if (phone) setValue('phone', removeNonNumeric(phone), opts)

    const address = data.address
    if (!address) return

    const stateCode = (address as { state_code?: string }).state_code ?? ''
    setValue('no_address', false)
    setValue(
      'address',
      {
        zip_code: address.zip_code ?? '',
        street: address.street ?? '',
        number: address.number ?? '',
        complement: address.complement ?? '',
        district: address.district ?? '',
        ibge_code: address.ibge_code ?? '',
        state_code: stateCode,
      },
      opts,
    )

    if (!stateCode && address.zip_code) {
      readZipcode(removeNonNumeric(address.zip_code))
        .then((cepData) => {
          setValue('address.state_code', cepData.state_code, opts)
          setValue('address.ibge_code', cepData.ibge_code, opts)
        })
        .catch(() => {})
    }
  }

  useEffect(() => {
    if (!shouldSearchSerpro || !serproData) return
    if (lastFilledTaxId.current === cleanTaxId) return
    lastFilledTaxId.current = cleanTaxId
    fillWithSerproData(serproData)
  }, [serproData, cleanTaxId, shouldSearchSerpro])

  const handleSuccess = (borrower: Borrower) => {
    toast.success(
      isEditMode
        ? 'Cliente atualizado com sucesso!'
        : 'Cliente cadastrado com sucesso!',
    )
    onSuccess?.(borrower)
    props.onClose()
  }

  const { mutate: createBorrower, isPending: isCreating } = useCreateBorrower({
    onSuccess: handleSuccess,
  })
  const { mutate: updateBorrower, isPending: isUpdating } = useUpdateBorrower({
    onSuccess: handleSuccess,
  })

  const handleSubmitBorrower = (data: BorrowerRegistrationFormData) => {
    if (isEditMode && borrower) {
      updateBorrower({
        companyKey: company.key,
        borrowerKey: borrower.key,
        borrowerData: buildUpdatePayload(data),
      })
    } else {
      createBorrower({
        companyKey: company.key,
        borrowerData: buildCreatePayload(data),
      })
    }
  }

  const resetValuesWhenChangeMode = () =>
    reset(
      isEditMode && borrower
        ? {
            tax_id_type: borrower.tax_id_type,
            tax_id: borrower.tax_id,
            name: borrower.name,
            email: borrower.email,
            phone: borrower.phone,
            no_address: !borrower.address,
            address: borrower.address ?? undefined,
          }
        : DEFAULT_VALUES,
    )

  const syncAddressWithNoAddressCheckbox = (checked: boolean) => {
    if (checked) setValue('address', undefined)
    else
      setValue(
        'address',
        isEditMode && borrower?.address
          ? borrower.address
          : { ...EMPTY_ADDRESS_FORM },
      )
  }

  useEffect(() => {
    if (!props.open) return
    lastFilledTaxId.current = ''
    resetValuesWhenChangeMode()
  }, [props.open, isEditMode, borrower, reset])

  return (
    <Dialog {...props}>
      <DialogTitle>{dialogTitle}</DialogTitle>

      <DialogContent style={{ paddingTop: '2px' }}>
        <FormProvider {...form}>
          <Stack spacing={2}>
            <Controller
              name="tax_id_type"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  select
                  disabled={isEditMode}
                  error={!!error}
                  helperText={error?.message}
                  onChange={(e) => {
                    field.onChange(e)
                    if (!isEditMode) setValue('tax_id', '')
                  }}
                >
                  <MenuItem value="cpf">Pessoa Física</MenuItem>
                  <MenuItem value="cnpj">Pessoa Jurídica</MenuItem>
                </TextField>
              )}
            />

            <Controller
              name="tax_id"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MaskedInput
                  {...field}
                  disabled={isEditMode}
                  label={`${watch('tax_id_type') === 'cpf' ? 'CPF' : 'CNPJ'} *`}
                  mask={
                    watch('tax_id_type') === 'cpf'
                      ? '000.000.000-00'
                      : CNPJ_IMASK_OPTS
                  }
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    endAdornment: isLoadingSerpro ? (
                      <InputAdornment position="end">
                        <CircularProgress size={18} />
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              )}
            />

            <Controller
              name="name"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Nome *"
                  placeholder="Nome"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="E-mail"
                  type="email"
                  placeholder="E-mail"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MaskedInput
                  {...field}
                  label="Telefone"
                  type="tel"
                  mask="(00) 00000-0000"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name="no_address"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => {
                        const checked = e.target.checked
                        field.onChange(checked)
                        syncAddressWithNoAddressCheckbox(checked)
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputRef={field.ref}
                    />
                  }
                  label="Não possui endereço"
                />
              )}
            />

            {!noAddress && <AddressForm />}
          </Stack>
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={props.onClose} variant="text">
          CANCELAR
        </Button>
        <LoadingButton
          loading={isCreating || isUpdating}
          onClick={handleSubmit(handleSubmitBorrower)}
        >
          {submitButtonText}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

type TBorrowerRegistrationDialogProps = DialogProps & {
  onClose: () => void
  onSuccess?: (borrower: Borrower) => void
  mode?: 'create' | 'edit'
  borrower?: Borrower
}

/** Valores iniciais do bloco de endereço ao desmarcar "Não possui endereço". */
const EMPTY_ADDRESS_FORM = {
  zip_code: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  ibge_code: '',
  state_code: '',
}

const DEFAULT_VALUES: BorrowerRegistrationFormData = {
  tax_id_type: 'cpf',
  tax_id: '',
  name: '',
  email: '',
  phone: '',
  no_address: true,
  address: undefined,
}

const buildCreatePayload = (data: BorrowerRegistrationFormData) => ({
  ...data,
  tax_id:
    data.tax_id_type === 'cnpj'
      ? sanitizeCnpj(data.tax_id || '')
      : removeNonNumeric(data.tax_id || ''),
  email: data.email || undefined,
  phone: data.phone || undefined,
  address: data.no_address ? undefined : data.address,
})

const buildUpdatePayload = (
  data: BorrowerRegistrationFormData,
): BorrowerDataUpdate => ({
  name: data.name,
  email: data.email || undefined,
  phone: data.phone || undefined,
  address: data.no_address ? undefined : data.address,
})
